import React, { useMemo } from "react";
import { Select } from "antd";
import styled from "styled-components/macro";
import { Icon } from "components/hds";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useCookies } from "react-cookie";
import { useFetch } from "use-http";
import getDomain, { getCookieDomain } from "utils/getDomain";
import organizationSubdomain from "utils/organizationSubdomain";
import useOrganizations from "contexts/Organizations/useOrganizations";
import useAuth from "contexts/Auth/useAuth";
import {
	StyledSwitcherContainer,
	StyledSwitcherNoMatch,
} from "common/styles/Switcher.styles";
import { compareByMatchStart, matchPosition } from "utils/switcherSearch";
import MenuItemStyles from "components/MenuItem/MenuItem.styles";
import MenuItem from "components/MenuItem/MenuItem";
import useTenant from "contexts/Tenant/useTenant";
import { Organization } from "contexts/Organizations/types";
import { CREATE_ORGANIZATION, PROJECTS } from "common/routes.constants";
import { WELCOME_MODAL_CASE } from "common/constants/cookies";
import { welcomePopupKinds } from "components/WelcomeModal/types";
import useConfirmLeaveScene from "hooks/useConfirmLeaveScene";
import useIsB2cContext from "hooks/useIsB2cContext";

const { Option } = Select;

/**
 * The text the switcher shows for an organization. A personal organization has
 * a translated label rather than its stored name, and a shortname is optional,
 * so neither `meta.name` nor `meta.shortname` alone is the visible string.
 */
const organizationLabel = (org: Organization, t: TFunction) =>
	org.isPersonal
		? String(t("app.global.b2c.personalOrganizationName"))
		: org.meta.shortname || org.meta.name || "";

const OrganizationSwitcher = () => {
	const {
		current,
		organizations,
		loading,
		setCurrentOrganization,
		member,
	} = useOrganizations();
	const { subdomainsSupported } = useTenant();
	// b2c users can't create an organization until they verify their email
	// (mirrors the backend's requireEmailVerified gate on POST /organizations).
	const { me } = useAuth();
	const canCreateOrganization = me?.profile?.emailVerified !== false;

	const isB2cContext = useIsB2cContext();

	const { t } = useTranslation();
	const history = useHistory();
	const { search } = useLocation();
	const confirmLeaveScene = useConfirmLeaveScene();

	const [, setCookie] = useCookies([WELCOME_MODAL_CASE]);

	// The list can be long, so typing filters it down to the organizations
	// whose label starts with the typed text. The whole list (up to 500) is
	// already loaded, so an organization that does not show up does not exist.
	const labelById = useMemo(
		() =>
			new Map(
				(organizations ?? []).map((org) => [
					org._id,
					organizationLabel(org, t),
				]),
			),
		[organizations, t],
	);

	const { put } = useFetch("users/me");

	const handleAddOrganizationClick = () => {
		history.push({
			pathname: `/${CREATE_ORGANIZATION}`,
			search,
		});
	};

	const switchOrganization = async (id: string) => {
		setCurrentOrganization(id);

		/* User is logging for the first time into
		   an organization that is not welcome org. */
		if (
			id !== process.env.REACT_APP_DEFAULT_ORG &&
			member?.user?.isFirstOrganizationLogin
		) {
			setCookie(
				WELCOME_MODAL_CASE,
				welcomePopupKinds.OrganizationInvite,
				{
					path: "/",
					domain: getCookieDomain(),
				},
			);

			/* Once that user see the welcome popup,
			   the flag of first login organizaton can be set to false. */
			await put({
				profile: { email: member?.user?.profile?.email },
				isFirstOrganizationLogin: false,
			});
		}

		if (subdomainsSupported) {
			const nextOrganization = organizations.find(
				(o) => o._id === id,
			) as Organization;

			const domain = getDomain();
			// organizationSubdomain: a personal space has no subdomain of its
			// own and lives on the shared one.
			const nextLocation = `${window.location.protocol}//${organizationSubdomain(nextOrganization)}.${domain}`;

			logger.info(
				"[OrganizationSwitcher] handleChangeOrganizationClick",
				{
					nextOrganization,
					nextLocation,
				},
			);

			window.location.assign(nextLocation);
			return false;
		}

		history.push({
			pathname: `/${PROJECTS}`,
			search,
		});
	};

	// The confirmation wraps the whole switch, not just the navigation at its
	// end: switchOrganization starts by moving the current organization and can
	// leave via window.location.assign (subdomain tenants), so there is no later
	// point at which backing out is still possible.
	const handleChangeOrganizationClick = (id: string) => {
		confirmLeaveScene(() => {
			void switchOrganization(id);
		});
	};

	// A switcher with a single entry has nothing to switch between — it just
	// invites a click that changes nothing, and puts an "organization" concept
	// in front of a consumer who has none.
	//
	// Two consumer shapes reach this, and only the org count tells them apart
	// from a business user: a regular b2c user has their personal space, and a
	// BMS endcustomer has the shared BMS org with their one project in it and no
	// personal space at all (see the isBmsB2cScope branch in hegiasGo's
	// auth.controller.js). A BMS ProfiPartner is b2c too but gets a personal org
	// alongside the BMS one, so he has two and keeps the switcher — which is
	// right, he genuinely moves between them.
	//
	// Still not "only one organization" on its own: a business user with a
	// single org can be invited into a second at any time, and a switcher that
	// vanishes and reappears is worse than one that is always there. Consumer
	// accounts do not grow that way.
	const onlyOrganization = organizations.length === 1;

	// Safe to render nothing: the grid child in Header.tsx is the wrapping
	// StyledOrganizationSwitcherContainer, not this component, so the
	// "organizations" column stays occupied by the logo and no track shifts.
	if (onlyOrganization && isB2cContext) {
		return null;
	}

	return (
		<StyledSwitcherContainer>
			<MenuItemStyles />
			<span className="label">
				{String(t("app.OrganizationSwitcher.label.switcher"))}
			</span>
			<Select
				value={current?._id}
				/* No inline width: it would beat the wider one in
				   Switcher.styles.tsx, which is what carries the chip's hit area
				   under the chevron. */

				onChange={handleChangeOrganizationClick}
				loading={loading}
				bordered={false}
				popupClassName="header-select-dropdown"
				showSearch
				filterOption={(input, option) =>
					matchPosition(labelById.get(String(option?.value)) ?? "", input) >= 0
				}
				/* Names that start with the typed text first, then the ones that
				   only contain it; each group keeps the list's alphabetical order
				   (the sort is stable). */
				filterSort={(a, b, { searchValue }) =>
					compareByMatchStart(
						labelById.get(String(a.value)) ?? "",
						labelById.get(String(b.value)) ?? "",
						searchValue,
					)
				}
				notFoundContent={
					<StyledSwitcherNoMatch>
						{String(t("app.OrganizationSwitcher.label.noMatch"))}
					</StyledSwitcherNoMatch>
				}
				/* The list is where names must not truncate, so it sizes to its
				   content. rc-select keeps the trigger width as a minimum; the cap
				   is in MenuItem.styles.tsx. */
				popupMatchSelectWidth={false}
				suffixIcon={<Icon name="ArrowDownFilledIcon" />}
				className="header-select"
				dropdownRender={(menu) => (
					<>
						{menu}
						{canCreateOrganization && (
							<StyledAddNewButton>
								<button
									type="button"
									onClick={handleAddOrganizationClick}
								>
									<span>
										<Icon name="PlusIcon" size="15px" />
									</span>
									{String(t(`app.global.btn.addItem`))}
								</button>
							</StyledAddNewButton>
						)}
					</>
				)}
			>
				{organizations.map((org) => (
					<Option key={org._id} value={org._id}>
						<MenuItem
							className={
								org.isPersonal
									? "switcher-item switcher-item--personal"
									: "switcher-item"
							}
							title={organizationLabel(org, t)}
							thumbnail={`${org.shortid}/${org.meta.avatar}`}
						/>
					</Option>
				))}
			</Select>
		</StyledSwitcherContainer>
	);
};

const StyledAddNewButton = styled.div`
	padding-top: 12px;
	display: flex;
	flex-wrap: nowrap;
	align-items: center;
	justify-content: center;
	border-top: 1px solid ${({ theme }) => theme.colors.grey300};
	> button {
		background-color: transparent;
		border: none;
		display: inline-flex;
		align-items: center;
		outline: none;
		&:hover {
			cursor: pointer;
			color: ${({ theme }) => theme.colors.primary};
		}
		span {
			align-items: center;
			justify-content: center;
			pointer-events: auto;
			border: none;
			overflow: hidden;
			outline: none;
			color: ${({ theme }) => theme.colors.grey800};
			background-color: ${({ theme }) => theme.colors.grey400};
			display: inline-flex;
			padding: 5px;
			border-radius: 50%;
			margin-right: 5px;
		}
	}
`;

export default OrganizationSwitcher;
